import * as update from 'immutability-helper';
import {Action as BaseAction, combineReducers} from 'small-redux';
import getIndexByValue from '../utils/getIndexByValue';
import {
	Action,
	ActionsCommon,
	ActionsFilter,
	ActionsListData,
	ActionsVisualData,
} from './Actions';
import State, {
	StateCommon,
	StateFilter,
	StateListData,
	StateVisualData,
	StateVisualStep,
} from './State';

/**
 * Initial app state.
 */
const initialState: State = {
	common: {
		loading: false,
		visualMode: true,
	},
	filter: {
		section: [],
		floor: {
			min: 0,
			max: 1,
			from: 0,
			to: 1,
		},
		roomType: [],
		squareTotal: {
			min: 0,
			max: 1,
			from: 0,
			to: 1,
		},
		totalCost: {
			min: 0,
			max: 1,
			from: 0,
			to: 1,
		},
	},
	listData: {
		items: [],
		itemsLeft: 0,
		nextItemsOffset: 0,
		listMode: false,
	},
	visualData: {
		overview: {
			view: '',
			nextView: '',
			image: '',
			map: [],
		},
		building: {
			building: '',
			view: '',
			nextView: '',
			image: '',
			map: [],
		},
		section: {
			section: '',
			view: '',
			nextView: '',
			image: '',
			map: [],
		},
		floor: {
			floor: 0,
			image: '',
			map: [],
		},
		step: StateVisualStep.OVERVIEW,
		prevStep: StateVisualStep.OVERVIEW,
		tooltip: {
			title: '',
			content: '',
			visible: false,
		},
		filterVisible: true,
	},
};

function common(
	state: StateCommon = initialState.common,
	action: Action<typeof ActionsCommon>,
): StateCommon
{
	switch ( action.type )
	{
		case ActionsCommon.COMMON$_SET_LOADING:
		case ActionsCommon.COMMON$_SET_VISUAL_MODE:
			return {
				...state,
				...action.payload,
			};
		
		default:
			return state;
	}
}

function filter(
	state: StateFilter = initialState.filter,
	action: Action<typeof ActionsFilter>,
): StateFilter
{
	switch ( action.type )
	{
		case ActionsFilter.FILTER$_SET_FILTER:
			return {
				...state,
				...action.payload,
			};
		
		case ActionsFilter.FILTER$_TOGGLE_SECTION:
		case ActionsFilter.FILTER$_TOGGLE_ROOM_TYPE:
			{
				const {name, value} = action.payload as {name: string, value: string};
				const index = getIndexByValue(
					(state as any)[name],
					value,
				);
				
				if ( index !== -1 )
				{
					return update(
						state,
						{[name]: {[index]: {checked: {$apply: ( x: boolean ) => !x}}}},
					);
				}
			}
			
			return state;
		
		case ActionsFilter.FILTER$_SET_FLOOR_FROM:
		case ActionsFilter.FILTER$_SET_SQUARE_TOTAL_FROM:
		case ActionsFilter.FILTER$_SET_TOTAL_COST_FROM:
			return update(
				state,
				{[action.payload.name]: {from: {$set: action.payload.from}}},
			);
		
		case ActionsFilter.FILTER$_SET_FLOOR_TO:
		case ActionsFilter.FILTER$_SET_SQUARE_TOTAL_TO:
		case ActionsFilter.FILTER$_SET_TOTAL_COST_TO:
			return update(
				state,
				{[action.payload.name]: {to: {$set: action.payload.to}}},
			);
		
		default:
			return state;
	}
}

function listData(
	state: StateListData = initialState.listData,
	action: Action<typeof ActionsListData>,
): StateListData
{
	switch ( action.type )
	{
		case ActionsListData.LIST_DATA$_SET_ITEMS:
		case ActionsListData.LIST_DATA$_SET_LIST_MODE:
			return {
				...state,
				...action.payload,
			};
		
		case ActionsListData.LIST_DATA$_APPEND_ITEMS:
			return update(
				state,
				{
					items: {$push: action.payload.items},
					itemsLeft: {$set: action.payload.itemsLeft},
					nextItemsOffset: {$set: action.payload.nextItemsOffset},
				},
			);
		
		default:
			return state;
	}
}

function visualData(
	state: StateVisualData = initialState.visualData,
	action: Action<typeof ActionsVisualData>,
): StateVisualData
{
	switch ( action.type )
	{
		case ActionsVisualData.VISUAL_DATA$_SET_OVERVIEW:
		case ActionsVisualData.VISUAL_DATA$_SET_BUILDING:
		case ActionsVisualData.VISUAL_DATA$_SET_SECTION:
		case ActionsVisualData.VISUAL_DATA$_SET_FLOOR:
		case ActionsVisualData.VISUAL_DATA$_SET_FILTER_VISIBLE:
			return {
				...state,
				...action.payload,
			};
		
		case ActionsVisualData.VISUAL_DATA$_SET_STEP:
			return {
				...state,
				...action.payload,
				prevStep: state.step,
			};
		
		case ActionsVisualData.VISUAL_DATA$_SET_TOOLTIP:
			return update(
				state,
				{
					tooltip: {
						$merge: action.payload,
					},
				},
			);
		
		default:
			return state;
	}
}

/**
 * App reducer.
 * 
 * @param state Current state.
 * @param action Required action.
 * @returns New state.
 */
const reducer = combineReducers<State, BaseAction>(
	{
		common,
		filter,
		listData,
		visualData,
	},
);

/**
 * Module.
 */
export {
	reducer as default,
};



// WEBPACK FOOTER //
// ./app/Routes/Choose/App/Store/reducer.ts